import { useIntl, FormattedMessage } from "react-intl"
import classNames from "classnames"
import { Spinner } from "../../../common"

/**
 * Natural-language search input + the three optional toggles the upstream
 * supports today (high_freshness, reveal_emails, reveal_phones) + a page-size
 * selector.
 *
 * Visually mirrors the existing platform search inputs (white card, navy
 * focus border, green primary action button).
 */
const OPTION_KEYS = [
  { key: "high_freshness", labelId: "sourcing.options.highFreshness" },
  { key: "reveal_emails", labelId: "sourcing.options.revealEmails" },
  { key: "reveal_phones", labelId: "sourcing.options.revealPhones" },
]

// Pearch fast-mode allows up to 100 per request. We stop there to keep the
// round-trip responsive and the credit cost predictable.
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const SearchConsole = ({
  query,
  setQuery,
  options,
  setOptions,
  pageSize,
  setPageSize,
  onSubmit,
  loading,
  estimatedCredits,
  estimatedProfiles,
}) => {
  const { formatMessage } = useIntl()

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && query.trim() && !loading) {
      e.preventDefault()
      onSubmit()
    }
  }

  return (
    <div className="sourcing-console">
      <div className="sourcing-console__input">
        <span className="sourcing-console__icon">{searchIcon}</span>

        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={formatMessage({ id: "sourcing.placeholder" })}
          disabled={loading}
        />

        <button
          type="button"
          className={classNames("btn primary btn--small", {
            disabled: loading || !query.trim(),
          })}
          onClick={onSubmit}
          disabled={loading || !query.trim()}
        >
          {loading
            ? <Spinner className="small" variant="light" />
            : <FormattedMessage id="sourcing.search" />
          }
        </button>
      </div>

      <div className="sourcing-console__opts">
        <div className="sourcing-console__chips">
          {OPTION_KEYS.map(({ key, labelId }) => (
            <button
              key={key}
              type="button"
              className={classNames("sourcing-chip", {
                "sourcing-chip--on": options[key],
              })}
              onClick={() => setOptions({ ...options, [key]: !options[key] })}
              disabled={loading}
            >
              <span className="sourcing-chip__dot" />
              <FormattedMessage id={labelId} />
            </button>
          ))}

          {/*
            Page-size selector lives alongside the option chips so it reads as
            part of "how the search runs". We surface the Pearch-allowed range
            (10/25/50/100) as a native <select> rather than a dropdown to stay
            visually light and keyboard-accessible out of the box.
          */}
          <label className="sourcing-chip sourcing-chip--select">
            <FormattedMessage id="sourcing.options.pageSize" />
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              disabled={loading}
              className="sourcing-chip__select"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
        </div>

        <span className="sourcing-console__cost">
          <FormattedMessage
            id="sourcing.options.estCost"
            values={{ credits: estimatedCredits, profiles: estimatedProfiles }}
          />
        </span>
      </div>
    </div>
  )
}

const searchIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="7" />
    <path d="m21 21-4.3-4.3" />
  </svg>
)

export default SearchConsole
